import {NotFoundError} from '@packages/utils';
import type {GetServerSideProps, GetServerSidePropsResult} from 'next';
import * as z from 'zod';
import type {PostSchema} from '../../core/entities/columns/schema';
import {getSpecialPost} from '../../core/repositories/columnRepository';
import {
  type GetUserDataForAnalyticsResponseType,
  getUserDataForAnalytics,
} from '../../core/repositories/usersMeRepository';
import {validateCookie} from '../../core/usecases/auth/checkOauthTokenExistence';
import {ColumnLayout} from '../../features/columns/components/Layout';
import {SpecialPage} from '../../features/columns/components/SpecialPage';
import {handleParsedContent} from '../../features/columns/usecases/handleParsedContent';
import {handlePostRedirect} from '../../features/columns/usecases/handlePostRedirect';
import {
  PostPageType,
  handlePostRedirectToPreviewPage,
} from '../../features/columns/usecases/handlePostRedirectToPreviewPage';
import {withHandler} from '../../server/token/withHandler';
import {ScriptForGA} from '../../shared/components/ScriptForGA';
import type {NextPageWithLayout} from '../_app';

type Props = {
  userDataForAnalytics?: GetUserDataForAnalyticsResponseType;
  slug: string;
  post: PostSchema;
  parsedContent: string;
  isWpContent: boolean;
  metaDescription?: string;
};

const QueryParameter = z.object({
  slug: z.string(),
  preview: z.string().optional(),
  preview_code: z.string().optional(),
});

type QueryParameter = z.infer<typeof QueryParameter>;

export const getServerSideProps: GetServerSideProps = withHandler<
  Props,
  'withOptionalSession' | 'withQuery',
  QueryParameter
>(
  {auth: 'optional', queryValidator: QueryParameter},
  async (context): Promise<GetServerSidePropsResult<Props>> => {
    const {slug, preview, preview_code} = context.validatedQuery;
    if (!slug) {
      return {
        notFound: true,
      };
    }

    const {cookies} = context.req;
    const {isValid} = validateCookie(cookies);

    const redirectToPreviewPage = await handlePostRedirectToPreviewPage({
      cookies,
      wpPreviewParam: preview ?? '',
      postPageType: PostPageType.SPECIAL,
      slug,
    });
    if (redirectToPreviewPage !== undefined) {
      return redirectToPreviewPage;
    }

    const getSpecialPostResponse = await getSpecialPost({
      cookies,
      slug,
      previewCode: preview_code ?? undefined,
    });

    if (
      !getSpecialPostResponse.ok &&
      getSpecialPostResponse.error === NotFoundError
    ) {
      return {
        redirect: {
          destination: '/column/categories',
          permanent: true,
        },
      };
    }

    if (!getSpecialPostResponse.ok) {
      throw new Error(getSpecialPostResponse.error);
    }

    const redirect = handlePostRedirect({
      post: getSpecialPostResponse.value.post,
      isSpecialPost: true,
    });
    if (redirect !== undefined) {
      return redirect;
    }

    const {parsedContent, metaDescription, isWpContent} =
      await handleParsedContent(
        getSpecialPostResponse.value.post.content,
        getSpecialPostResponse.value.post.contentHTML,
        getSpecialPostResponse.value.post.description,
      );

    const props: Props = {
      slug,
      post: getSpecialPostResponse.value.post,
      parsedContent,
      isWpContent,
      metaDescription,
    };

    if (!isValid) return {props};

    const userDataResponse = await getUserDataForAnalytics({
      cookies,
    });
    if (!userDataResponse.ok) {
      return {
        props,
      };
    }

    return {
      props: {
        ...props,
        userDataForAnalytics: userDataResponse.value,
      },
    };
  },
);

const Page: NextPageWithLayout<Props> = ({
  userDataForAnalytics,
  slug,
  post,
  parsedContent,
  isWpContent,
  metaDescription,
}: Props) => {
  return (
    <>
      <SpecialPage
        slug={slug}
        post={post}
        parsedContent={parsedContent}
        isWpContent={isWpContent}
        metaDescription={metaDescription}
      />
      <ScriptForGA userDataForAnalytics={userDataForAnalytics} />
    </>
  );
};

export default Page;

Page.getLayout = (page) => <ColumnLayout>{page}</ColumnLayout>;
